import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { ImageViewer } from '@/components/ui/ImageViewer';
import { 
  ZoomIn,
  Grid3X3,
  Eye
} from 'lucide-react';
import { getImageMenuItems, getImageMenuDisplayConfig } from '@/lib/firebase/imageMenus';
import type { ImageMenuItem, ImageMenuDisplayConfig } from '@/types';

interface ImageMenuPreviewProps {
  restaurantId: string;
  hasDigitalMenu: boolean;
  hasAvailableDigitalItems: boolean;
  className?: string;
}

/**
 * ImageMenuPreview - Customer-facing image menu display
 * Features:
 * - Thumbnail row view when combined with digital menu
 * - Full grid view when standalone or digital menu unavailable
 * - Image expansion with zoom and navigation
 * - Mobile-optimized touch gestures
 * - Dynamic layout based on menu availability
 */
const ImageMenuPreview: React.FC<ImageMenuPreviewProps> = ({
  restaurantId,
  hasDigitalMenu,
  hasAvailableDigitalItems,
  className = ''
}) => {
  const [images, setImages] = useState<ImageMenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displayConfig, setDisplayConfig] = useState<ImageMenuDisplayConfig | null>(null);
  const [isGridExpanded, setIsGridExpanded] = useState(false);
  const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(null);

  const loadImageMenu = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const imageMenuItems = await getImageMenuItems(restaurantId);
      setImages(imageMenuItems);
      
      const config = getImageMenuDisplayConfig(
        imageMenuItems.length > 0,
        hasDigitalMenu,
        hasAvailableDigitalItems
      );
      setDisplayConfig(config);
      
      // Auto-expand grid if it should show directly
      setIsGridExpanded(config.showGridDirectly);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load image menu');
    } finally {
      setLoading(false);
    }
  }, [restaurantId, hasDigitalMenu, hasAvailableDigitalItems]);

  useEffect(() => {
    loadImageMenu();
  }, [loadImageMenu]);

  const expandImage = (index: number) => {
    setExpandedImageIndex(index);
  };

  const closeExpandedImage = () => {
    setExpandedImageIndex(null);
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (expandedImageIndex === null) return;
    
    if (direction === 'prev') {
      setExpandedImageIndex(
        expandedImageIndex > 0 ? expandedImageIndex - 1 : images.length - 1
      );
    } else {
      setExpandedImageIndex(
        expandedImageIndex < images.length - 1 ? expandedImageIndex + 1 : 0
      );
    }
  };

  const getThumbnailCount = () => {
    if (!displayConfig) return 0;
    return window.innerWidth >= 768 
      ? displayConfig.thumbnailCount.desktop 
      : displayConfig.thumbnailCount.mobile;
  };

  const displayedThumbnails = images.slice(0, getThumbnailCount());
  const hasMoreImages = images.length > getThumbnailCount();

  // Don't render if no images or loading
  if (loading || !displayConfig || images.length === 0) {
    return null;
  }

  // Error state
  if (error) {
    return null; // Fail silently for customer-facing component
  }

  // Helper function to scroll to grid section
  const scrollToGrid = () => {
    setIsGridExpanded(true);
    // Small delay to ensure DOM update, then scroll
    setTimeout(() => {
      const gridElement = document.getElementById('image-menu-grid');
      if (gridElement) {
        gridElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };  return (
    <div className={className}>
      {/* Thumbnail Row View */}
      {displayConfig.showThumbnailRow && !isGridExpanded && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-4 border-b border-gray-100"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Menu Photos
            </h3>
            {hasMoreImages && (
              <Button
                variant="ghost"
                size="sm"
                onClick={scrollToGrid}
                rightIcon={<Grid3X3 className="w-4 h-4" />}
              >
                View All ({images.length})
              </Button>
            )}
          </div>
          
          <div className="flex space-x-3 overflow-x-auto pb-2">
            {displayedThumbnails.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="relative flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32 bg-gray-100 rounded-xl overflow-hidden cursor-pointer group"
                onClick={() => expandImage(index)}
              >
                <img
                  src={image.thumbnailUrl || image.imageUrl}
                  alt={`Menu photo ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                  <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </motion.div>
            ))}
            
            {hasMoreImages && (
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: displayedThumbnails.length * 0.1 }}
                onClick={scrollToGrid}
                className="flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32 bg-gray-100 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors"
              >
                <Grid3X3 className="w-6 h-6 mb-1" />
                <span className="text-xs font-medium">+{images.length - getThumbnailCount()}</span>
              </motion.button>
            )}
          </div>
        </motion.div>
      )}

      {/* Full Grid View */}
      {(displayConfig.showGridDirectly || isGridExpanded) && (
        <motion.div
          id="image-menu-grid"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-4 border-b border-gray-100"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {displayConfig.showGridDirectly ? 'Menu Photos' : 'All Menu Photos'}
            </h3>
            {!displayConfig.showGridDirectly && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsGridExpanded(false)}
                leftIcon={<Eye className="w-4 h-4" />}
              >
                Show Less
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            {images.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden cursor-pointer group"
                onClick={() => expandImage(index)}
              >
                <img
                  src={image.thumbnailUrl || image.imageUrl}
                  alt={`Menu photo ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                  <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                
                {/* Image number indicator */}
                <div className="absolute top-2 right-2 w-6 h-6 bg-black bg-opacity-50 text-white text-xs rounded-full flex items-center justify-center font-medium">
                  {index + 1}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Image Viewer */}
      <ImageViewer
        images={images.map(img => ({
          id: img.id,
          imageUrl: img.imageUrl,
          alt: `Menu photo`
        }))}
        currentIndex={expandedImageIndex}
        onClose={closeExpandedImage}
        onNavigate={navigateImage}
        showInfo={true}
      />
    </div>
  );
};

export default ImageMenuPreview;